import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  type MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_VisibilityState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { CostObject } from "../../Types/CostObject.type";
import { formatDate } from "../../Helpers/DateFormat.helper";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";
import { CommonToolbarInternalActions } from "../../Components/SharedComponents/CommonTableToolbarActions";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useViewRow } from "../../Components/ViewDetails/viewDetails";
import RowDetailsModal from "../../Components/ViewDetails/RowDetailsModal";




// const StatusChip = ({ status }: { status: string }) => {
// const colorMap: Record<string, "default" | "warning" | "success" | "error" | "info"> = {
//   PENDING: "warning",
//   COMMITTED: "info",
//   APPROVED: "success",
//   REJECTED: "error",
// };
//   return (
//     <Chip
//       label={status.charAt(0).toUpperCase() + status.slice(1)}
//       color={colorMap[status] || "default"}
//       size="small"
//       sx={{ fontSize: "0.65rem", height: "20px" }}
//     />
//   );
// };

const createColumnConfig = (
  accessorKey: string,
  header: string,
  validationErrors: Record<string, string | undefined>,
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>,
  overrides: Partial<MRT_ColumnDef<any>> = {}
): MRT_ColumnDef<any> => ({
  accessorKey,
  header,
  enableEditing: false,
  filterVariant: "text",
  muiTableBodyCellProps: ({ row, table }) => ({
    onDoubleClick: (e: any) => e.stopPropagation(),
    sx: {
      cursor: "pointer",
      "&:hover": { backgroundColor: "rgba(44, 4, 4, 0.05)" },
      ...(table.getState().editingRow?.id === row.id && {
        backgroundColor: "rgba(0, 128, 0, 0.1)",
        transition: "background-color 0.3s ease",
      }),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  }),
  muiEditTextFieldProps: {
    name: accessorKey,
    required: true,
    error: !!validationErrors?.[accessorKey],
    helperText: validationErrors?.[accessorKey],
    onFocus: () =>
      setValidationErrors((prev) => ({ ...prev, [accessorKey]: undefined })),
  },
  ...overrides,
});

// ─── Table Inner Component ────────────────────────────────────────────────────
const CostObjectTableInner = ({
  dataVersion,
  setDataVersion,
  setCopiedData,
  setEditMode,
  setEditedData,
  externalFilters = {},
}: {
  dataVersion: number;
  setDataVersion: React.Dispatch<React.SetStateAction<number>>;
  setCopiedData: (data: any) => void;
  setEditMode: (v: boolean) => void;
  setEditedData: (data: any) => void;
  externalFilters?: Record<string, any>;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({});
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [filterModes, setFilterModes] = useState<{ mode: string; id: string; value: unknown }[]>([]);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const { viewModalOpen, selectedRowData, handleViewRow, handleCloseViewModal } = useViewRow();
  
  useEffect(() => {
    const stored = {
      columnFilters: sessionStorage.getItem("mrt_co_columnFilters"),
      columnVisibility: sessionStorage.getItem("mrt_co_columnVisibility"),
      density: sessionStorage.getItem("mrt_co_density"),
      globalFilter: sessionStorage.getItem("mrt_co_globalFilter"),
      showGlobalFilter: sessionStorage.getItem("mrt_co_showGlobalFilter"),
      showColumnFilters: sessionStorage.getItem("mrt_co_showColumnFilters"),
      sorting: sessionStorage.getItem("mrt_co_sorting"),
    };
    if (stored.columnFilters) setColumnFilters(JSON.parse(stored.columnFilters));
    if (stored.columnVisibility) setColumnVisibility(JSON.parse(stored.columnVisibility));
    if (stored.density) setDensity(JSON.parse(stored.density));
    if (stored.globalFilter) setGlobalFilter(JSON.parse(stored.globalFilter) || undefined);
    if (stored.showGlobalFilter) setShowGlobalFilter(JSON.parse(stored.showGlobalFilter));
    if (stored.showColumnFilters) setShowColumnFilters(JSON.parse(stored.showColumnFilters));
    if (stored.sorting) setSorting(JSON.parse(stored.sorting));
    isFirstRender.current = false;
  }, []);

  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_columnFilters", JSON.stringify(columnFilters)); }, [columnFilters]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_columnVisibility", JSON.stringify(columnVisibility)); }, [columnVisibility]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_density", JSON.stringify(density)); }, [density]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_globalFilter", JSON.stringify(globalFilter ?? "")); }, [globalFilter]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_showGlobalFilter", JSON.stringify(showGlobalFilter)); }, [showGlobalFilter]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_showColumnFilters", JSON.stringify(showColumnFilters)); }, [showColumnFilters]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_sorting", JSON.stringify(sorting)); }, [sorting]);

  // ─── Columns ─────────────────────────────────────────────────────────────
  const columns = useMemo<MRT_ColumnDef<CostObject>[]>(
    () => [
      createColumnConfig("cost_booking_id", "ID", validationErrors, setValidationErrors, {
        size: 120,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#555" }}>
            {cell.getValue()}
          </span>
        ),
      }),

      createColumnConfig("effective_date", "Effective Date", validationErrors, setValidationErrors, {
        size: 190,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem" }}>{formatDate(cell.getValue()) || "—"}</span>
        ),
      }),
      createColumnConfig("book_name", "Book", validationErrors, setValidationErrors, {
        size: 150,
        Cell: ({ row }: any) => (
          <span style={{ fontSize: "0.72rem" }}>
            {row.original.book_name || row.original.book || "—"}
          </span>
        ),
      }),
      createColumnConfig("strategy_name", "Strategy", validationErrors, setValidationErrors, {
        size: 160,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
        ),
      }), 
      createColumnConfig("cost_type_name", "Cost Type", validationErrors, setValidationErrors, {
        size: 170,
        Cell: ({ row }: any) => (
          <span style={{ fontSize: "0.72rem", textTransform: "capitalize" }}>
            {row.original.cost_type_name}
          </span>
        ),
      }),
      createColumnConfig("PnL_Category_name", "P&L Category", validationErrors, setValidationErrors, {
        size: 190,
        Cell: ({ row }: any) => (
          <span style={{ fontSize: "0.72rem", textTransform: "capitalize" }}>
            {row.original.PnL_Category_name || row.original.PnL_Category || "—"}
          </span>
        ),
      }), 
      createColumnConfig("amount", "Amount", validationErrors, setValidationErrors, {
        size: 150,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", fontWeight: "bold" }}>
            {Number(cell.getValue()).toLocaleString()}
          </span>
        ),
      }),
      createColumnConfig("currency", "Currency", validationErrors, setValidationErrors, {
        size: 170,
        Cell: ({ row }: any) => (
          <span style={{ fontSize: "0.72rem" }}>
            {row.original.currency_name || row.original.currency || "—"}
          </span>
        ),
      }),
      createColumnConfig("exclude_from_pnl", "Incl. PNL", validationErrors, setValidationErrors, {
        size: 170,
        accessorFn: (row: CostObject) => (row.exclude_from_pnl ? "Yes" : "No"),  // ADD
        Cell: ({ cell }: any) => (
          <span style={{ color: cell.getValue() === "Yes" ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
            {cell.getValue()}
          </span>
        ),
      }),

      createColumnConfig("Booking_Type", "Type", validationErrors, setValidationErrors, {
        size: 160,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem" }}>
            {cell.getValue() === "Lump Sum" ? "Lump Sum" : cell.getValue()}
          </span>
        ),
      }),
      // createColumnConfig("due_date", "Due Date", validationErrors, setValidationErrors, {
      //   size: 210,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem" }}>{formatDate(cell.getValue()) || "—"}</span>
      //   ),
      // }),
      // createColumnConfig("trader_name", "Trader", validationErrors, setValidationErrors, {
      //   size: 200,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
      //   ),
      // }),
      // createColumnConfig("product_name", "Product", validationErrors, setValidationErrors, {
      //   size: 210,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem", textTransform: "capitalize" }}>
      //       {cell.getValue() || "—"}
      //     </span>
      //   ),
      // }),

      // createColumnConfig("clearer_name", "Clearer", validationErrors, setValidationErrors, {
      //   size: 190,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
      //   ),
      // }),
      // createColumnConfig("broker_name", "Broker", validationErrors, setValidationErrors, {
      //   size: 210,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
      //   ),
      // }),
      // createColumnConfig("counterparty_name", "Counterparty", validationErrors, setValidationErrors, {
      //   size: 180,
      //   Cell: ({ cell }: any) => (
      //     <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
      //   ),
      // }),
      createColumnConfig("requires_settlement", "Req. Settlement", validationErrors, setValidationErrors, {
        size: 210,
        accessorFn: (row: CostObject) => (row.requires_settlement ? "Yes" : "No"),  // ADD
        Cell: ({ cell }: any) => (
          <span style={{ color: cell.getValue() === "Yes" ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
            {cell.getValue()}
          </span>
        ),
      }),
      createColumnConfig("description", "Description", validationErrors, setValidationErrors, {
        size: 210,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: cell.getValue() ? "inherit" : "#999" }}>
            {cell.getValue() || "—"}
          </span>
        ),
      }),
      createColumnConfig("status", "Status", validationErrors, setValidationErrors, {
        size: 160,
        accessorFn: (row: CostObject) => {
          const status = row.status;
          return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "—";
        },
        Cell: ({ cell }: any) => {
          const status = cell.getValue() as string;
          const colorMap: Record<string, string> = {
            Pending: "#ed6c02",
            Committed: "#0288d1",
            Approved: "#2e7d32",
            Rejected: "#d32f2f",
          };
          return (
            <span style={{ fontSize: "0.72rem", fontWeight: 600, color: colorMap[status] || "#555" }}>
              {status}
            </span>
          );
        },
      }),

      createColumnConfig("updated_at_over_ridden", "Updated At", validationErrors, setValidationErrors, {
        size: 170,
        filterFn: "contains",
        accessorFn: (row: CostObject) => {
          const raw = (row as any).updated_at_over_ridden;
          if (!raw) return "—";
          return String(raw).slice(0, 10);
        },
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: cell.getValue() !== "—" ? "inherit" : "#999" }}>
            {cell.getValue() !== "—" ? formatDate(cell.getValue()) : "—"}
          </span>
        ),
      }), 
      createColumnConfig("created_user", "Updated By", validationErrors, setValidationErrors, {
        size: 180,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: cell.getValue() ? "inherit" : "#999" }}>
            {cell.getValue() || "—"}
          </span>
        ),
      }),
    ],
    [validationErrors]
  );

// ─── Helper: cartesian product of multi-value filters ─────────────────────
  const buildFilterCombinations = (
    multiEntries: [string, any[]][]
  ): Record<string, any>[] => {
    return multiEntries.reduce<Record<string, any>[]>(
      (acc, [key, values]) => {
        const next: Record<string, any>[] = [];
        for (const combo of acc) {
          for (const v of values) {
            next.push({ ...combo, [key]: v });
          }
        }
        return next;
      },
      [{}]
    );
  };

  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useQuery<{ results: CostObject[]; totalCount: number }>({
    queryKey: ["cost_objects", pagination, sorting, filterModes, dataVersion, externalFilters],
    queryFn: async () => {
      const clientRanges: Record<string, [string, string]> = {};
      const cleanedFilters: Record<string, any> = {};
      Object.entries(externalFilters || {}).forEach(([k, v]) => {
        if (k.startsWith("__clientRange_")) {
          clientRanges[k.replace("__clientRange_", "")] = v as [string, string];
        } else {
          cleanedFilters[k] = v;
        }
      });

      const allEntries = Object.entries(cleanedFilters).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      );
      const singleEntries = allEntries.filter(
        ([, v]) => !(Array.isArray(v) && v.length > 1)
      );
      const multiEntries = allEntries.filter(
        ([, v]) => Array.isArray(v) && v.length > 1
      ) as [string, any[]][];

      const hasClientRanges = Object.keys(clientRanges).length > 0;

      const appendCommonParams = (params: URLSearchParams) => {
        if (filterModes.length > 0) {
          filterModes.forEach((e) => {
            params.append(`fieldName_${e.id}`, `${String(e.value)}_${e.mode}`);
          });
        }
        singleEntries.forEach(([key, value]) => {
          if (Array.isArray(value)) {
            // single-item array
            params.append(key, String(value[0]));
          } else {
            params.append(key, String(value));
          }
        });
        if (sorting.length > 0) {
          params.append(
            `sortField_${sorting[0].id}`,
            `${sorting[0].desc ? "desc" : "asc"}_sorting`
          );
        }
      };

      // ── No multi-select fields: original single-request, server-paginated path ──
      if (multiEntries.length === 0 && !hasClientRanges) {
        const params = new URLSearchParams({
          page: `${pagination.pageIndex + 1}`,
          page_size: `${pagination.pageSize}`,
        });
        appendCommonParams(params);

        const response = await axiosInstance.get(
          `${backendbaseUrl}/paper-app/cost-booking/?${params.toString()}`
        );
        const results: CostObject[] = response.data?.results ?? response.data ?? [];
        const count: number = response.data?.count ?? results.length;
        return { results, totalCount: count };
      }

      // ── Multi-select present: fetch every value-combination, merge, dedupe, paginate client-side ──
      const combinations = buildFilterCombinations(multiEntries);

      const requests = combinations.map((combo) => {
        const params = new URLSearchParams({
          page: "1",
          page_size: "10000",
        });
        appendCommonParams(params);
        Object.entries(combo).forEach(([key, value]) => {
          params.append(key, String(value));
        });
        return axiosInstance.get(
          `${backendbaseUrl}/paper-app/cost-booking/?${params.toString()}`
        );
      });

      const responses = await Promise.all(requests);

      const merged = new Map<string, CostObject>();
      responses.forEach((response) => {
        const results: CostObject[] = response.data?.results ?? response.data ?? [];
        results.forEach((row: any) => {
          const key = String(row.id ?? row.cost_booking_id);
          if (!merged.has(key)) merged.set(key, row);
        });
      });

      let mergedArray = Array.from(merged.values());

      if (hasClientRanges) {
        mergedArray = mergedArray.filter((row: any) =>
          Object.entries(clientRanges).every(([field, [start, end]]) => {
            const val = row[field]; 
            if (!val) return false;
            const dateOnly = String(val).slice(0, 10); 
            return dateOnly >= start && dateOnly <= end;
          })
        );
      }

      if (sorting.length > 0) {
        const { id, desc } = sorting[0];
        mergedArray = mergedArray.sort((a: any, b: any) => {
          const av = a[id];
          const bv = b[id];
          if (av == null && bv == null) return 0;
          if (av == null) return desc ? 1 : -1;
          if (bv == null) return desc ? -1 : 1;
          if (av < bv) return desc ? 1 : -1;
          if (av > bv) return desc ? -1 : 1;
          return 0;
        });
      }

      const count = mergedArray.length;
      const start = pagination.pageIndex * pagination.pageSize;
      const pageSlice = mergedArray.slice(start, start + pagination.pageSize);

      return { results: pageSlice, totalCount: count };
    },
    refetchOnWindowFocus: false,
    placeholderData: (old) => old,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const { mutateAsync: deleteItem, isPending: isDeletingUser } = useMutation({
    mutationFn: async (id: string) => {
      const response = await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/cost-booking/Bulk-Delete/`,
        { data: { id: [id] } }
      );
      return response.data;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["cost_objects"] });
      const prev = queryClient.getQueryData(["cost_objects"]);
      return { prev };
    },
    onError: (err, id, context: any) => {
      queryClient.setQueryData(["cost_objects"], context?.prev);
      toast.error("Failed to delete. Please try again.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cost_objects"] });
    },
  });

  const contractEditFunctionality = (row: MRT_Row<CostObject>) => {
    const rowData = row.original;
    const textToCopy = JSON.stringify(rowData, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error("Clipboard write failed:", err);
      });
    }
    setCopiedData(rowData);
    setEditMode(true);
    setEditedData(rowData);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopyRow = (row: MRT_Row<CostObject>) => {
    if (!row) return;
    const rowData = row.original;
    const textToCopy = JSON.stringify(rowData, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error("Clipboard write failed:", err);
      });
    }
    setCopiedData(rowData);
    setEditMode(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Copied and pasted successfully");
  };

  const openDeleteConfirmModal = (row: MRT_Row<CostObject>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this Cost Object?",
      content: "Deleting...",
      onOk: () => {
        deleteItem(row.original.id)
          .then((data: any) => {
            toast.success(data?.message ||"Cost Object deleted successfully!");
            setDataVersion((prev) => prev + 1);
          })
          .catch(() => {
            toast.error("Failed to delete. Please try again.");
          });
      },
    });
  };

  // ─── Bulk delete — API ────────────────────────────────────────────────────
  const handleBulkDelete = async (rows: MRT_Row<CostObject>[]) => {
    const rowIds = rows.map((row) => row.original.id);
    if (rowIds.length === 0) {
      toast.warn("Please select at least one Cost Object to delete.");
      return;
    }

    showDeleteConfirmModal({
      title: `Delete ${rowIds.length} selected Cost Object(s)?`,
      content: `This will permanently delete ${rowIds.length} record(s). Continue?`,
      onOk: async () => {
        const loadingToast = toast.loading(`Deleting ${rowIds.length} Cost Object(s)...`);
        try {
          const response = await axiosInstance.delete(
            `${backendbaseUrl}/paper-app/cost-booking/Bulk-Delete/`,
            { data: { id: rowIds } }
          );
          queryClient.invalidateQueries({ queryKey: ["cost_objects"] });
          table.setRowSelection({});
          setDataVersion((prev) => prev + 1);
          toast.success(response.data?.message ||`Successfully deleted ${rowIds.length} Cost Object(s)`);
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message ||
            "Failed to delete Cost Objects";
          toast.error(`Deletion failed: ${errorMessage}`);
        } finally {
          toast.dismiss(loadingToast);
        }
      },
    });
  };

  // ─── Export all data ──────────────────────────────────────────────────────
  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/cost-booking/`,
        { params: { page_size: 10000 } }
      );
      const all: CostObject[] = response.data?.results ?? response.data ?? [];
      if (all.length === 0) { toast.warn("No data to export"); return; }
      const headers = Object.keys(all[0]).join(",");
      const rows = all.map((row) =>
        Object.values(row).map((v) => `"${v ?? ""}"`).join(",")
      );
      const csv = [headers, ...rows].join("\n");
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cost_objects.csv");
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("Cost Object data downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download data");
    }
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log(rowData);
  };

  const handleHistory = () => {
    navigate("/settings/log/history/costObjects");
  };

  const openrowhistory = (row: any) => {
    navigate(`/settings/log/history/costObjects/${row.id}`);
  };

  // ─── Context menu ─────────────────────────────────────────────────────────
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };
  const handleClose = () => setContextMenu(null);
  const handleResizing = () => { setShowRowResizing(!showRowResizing); handleClose(); };

  // ─── Table instance ───────────────────────────────────────────────────────
  const table = useMaterialReactTable<CostObject>({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableColumnFilterModes: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    enableEditing: true,
    manualPagination: true,
    manualSorting: false,
    manualFiltering: true,
    rowCount: data?.totalCount ?? 0,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    data: data?.results || [],
    initialState: { showColumnFilters },

    state: {
      columnVisibility,
      sorting,
      columnFilters,
      pagination,
      isLoading: isLoadingUsers,
      isSaving: isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
      showColumnFilters,
      showGlobalFilter,
    },

    enableColumnOrdering: true,
    enableRowOrdering: false,

    filterFns: {
      customFilterFn: (row, id, filterValue) => row.getValue(id) === filterValue,
    },

    muiTopToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableContainerProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      sx: {
        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
        ...(table.getState().editingRow?.id === row.id && {
          backgroundColor: "rgba(0, 128, 0, 0.1)",
          transition: "background-color 0.3s ease",
        }),
      },
    }),

    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: "0", border: "0" },
    },
    muiToolbarAlertBannerProps: isLoadingUsersError
      ? { color: "error", children: "Error loading data" }
      : undefined,
    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal" },
    },

    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 155,
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
      },
    },

    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: async ({ table }) => { table.setEditingRow(null); },

    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="file"
          accept=".csv"
          id="upload-cost-object-csv"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const formData = new FormData();
              formData.append("file", file);
              Swal.fire({
                icon: "info",
                title: "Import",
                text: "CSV import will be connected to API when endpoint is ready.",
                timer: 2000,
                showConfirmButton: false,
              });
              e.target.value = "";
            }
          }}
        />

        {/* Import CSV */}
        {/* <Button
          variant="text"
          color="success"
          startIcon={<UploadFileIcon />}
          onClick={() => document.getElementById("upload-cost-object-csv")?.click()}
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.8, px: 1.2, borderRadius: "6px" }}
        >
          Import CSV
        </Button> */}

        {/* Export All Data */}
        <Button
          onClick={handleDataDownload}
          startIcon={<BrowserUpdatedIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Export All Data
        </Button>

        {/* History */}
        {/* <Button
          onClick={handleHistory}
          startIcon={<HistoryIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          History
        </Button> */}

        {/* Export Selected Rows */}
        {/* <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Export Selected Rows
        </Button> */}

        {/* Bulk Delete */}
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),

    renderRowActions: ({ row }) => (
      <Box>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => contractEditFunctionality(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title="History" className="History">
          <IconButton color="error" onClick={() => openrowhistory(row.original)}>
            <HistoryIcon />
          </IconButton>
        </Tooltip> */}
        <Tooltip title="View Details">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleViewRow(row);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  useEffect(() => {
    const transformedFilters = columnFilters.map((filter) => {
      let id = filter.id;
      if (id === "PnL_Category_name") {
        id = "PnL_Category";                 
      }

      if (id === "requires_settlement" || id === "exclude_from_pnl") {
        const val = String(filter.value).toLowerCase().trim();
        return {
          ...filter,
          id,
          value: val === "yes" ? "true" : val === "no" ? "false" : filter.value,
          mode: "contains",
        };
      }

      return {
        ...filter,
        id,
        mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
      };
    });
    setFilterModes(transformedFilters);
  }, [columnFilters, table]);
  return (
    <div className="table-ad__wrapper" onContextMenu={handleContextMenu}>
      <div className="table-ad__wrapperCard">
        <MaterialReactTable table={table} />

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleResizing}>
            {showRowResizing ? "Disable Resizing" : "Enable Resizing"}
          </MenuItem>
        </Menu>
        <RowDetailsModal
          open={viewModalOpen}
          onClose={handleCloseViewModal}
          rowData={selectedRowData}
        />
      </div>
    </div>
  );
};

const queryClient = new QueryClient();

export default function CostObjectTable({
  dataVersion,
  setDataVersion,
  setCopiedData,
  setEditMode,
  setEditedData,
  externalFilters,
}: {
  dataVersion: number;
  setDataVersion: React.Dispatch<React.SetStateAction<number>>;
  setCopiedData: (data: any) => void;
  setEditMode: (v: boolean) => void;
  setEditedData: (data: any) => void;
  externalFilters?: Record<string, any>;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <CostObjectTableInner
        dataVersion={dataVersion}
        setDataVersion={setDataVersion}
        setCopiedData={setCopiedData}
        setEditMode={setEditMode}
        setEditedData={setEditedData}
        externalFilters={externalFilters}
      />
    </QueryClientProvider>
  );
}